import React from "react";

import type { SonicsLensId } from "./AudioWitnessBar";

type NarrativeSonicsLensTagsProps = {
  availableLensIds: SonicsLensId[];
  activeLensId?: SonicsLensId | null;
  onToggleLens: (lensId: SonicsLensId) => void;
  lightMode?: boolean;
};

const LENS_LABELS: Record<SonicsLensId, string> = {
  arc: "ARC",
  fx: "FX",
  continuity: "CONT",
  aiPreview: "AI",
  audit: "AUDIT",
  motif: "MOTIF",
  pulse: "PULSE",
};

const LENS_VISUALS: Record<
  SonicsLensId,
  {
    accent: string;
    border: string;
    glow: string;
    lightFill: string;
    lightText: string;
    darkFill: string;
    darkText: string;
  }
> = {
  arc: {
    accent: "rgba(45,212,191,0.96)",
    border: "rgba(45,212,191,0.38)",
    glow: "rgba(20,184,166,0.24)",
    lightFill: "linear-gradient(180deg, rgba(204,251,241,0.9), rgba(255,255,255,0.22))",
    lightText: "rgba(17,94,89,0.98)",
    darkFill: "linear-gradient(180deg, rgba(45,212,191,0.24), rgba(19,78,74,0.84))",
    darkText: "rgba(209,250,229,0.98)",
  },
  fx: {
    accent: "rgba(56,189,248,0.96)",
    border: "rgba(56,189,248,0.38)",
    glow: "rgba(14,165,233,0.24)",
    lightFill: "linear-gradient(180deg, rgba(186,230,253,0.9), rgba(255,255,255,0.22))",
    lightText: "rgba(12,74,110,0.98)",
    darkFill: "linear-gradient(180deg, rgba(34,211,238,0.24), rgba(8,47,73,0.82))",
    darkText: "rgba(207,250,254,0.98)",
  },
  continuity: {
    accent: "rgba(251,191,36,0.96)",
    border: "rgba(251,191,36,0.38)",
    glow: "rgba(245,158,11,0.22)",
    lightFill: "linear-gradient(180deg, rgba(254,240,138,0.88), rgba(255,255,255,0.22))",
    lightText: "rgba(120,53,15,0.98)",
    darkFill: "linear-gradient(180deg, rgba(251,191,36,0.22), rgba(120,53,15,0.82))",
    darkText: "rgba(254,243,199,0.98)",
  },
  aiPreview: {
    accent: "rgba(192,132,252,0.96)",
    border: "rgba(192,132,252,0.38)",
    glow: "rgba(168,85,247,0.24)",
    lightFill: "linear-gradient(180deg, rgba(233,213,255,0.9), rgba(255,255,255,0.22))",
    lightText: "rgba(107,33,168,0.98)",
    darkFill: "linear-gradient(180deg, rgba(192,132,252,0.24), rgba(76,29,149,0.84))",
    darkText: "rgba(243,232,255,0.98)",
  },
  audit: {
    accent: "rgba(148,163,184,0.96)",
    border: "rgba(148,163,184,0.3)",
    glow: "rgba(100,116,139,0.18)",
    lightFill: "linear-gradient(180deg, rgba(226,232,240,0.88), rgba(255,255,255,0.2))",
    lightText: "rgba(51,65,85,0.96)",
    darkFill: "linear-gradient(180deg, rgba(148,163,184,0.18), rgba(30,41,59,0.82))",
    darkText: "rgba(226,232,240,0.96)",
  },
  motif: {
    accent: "rgba(244,114,182,0.96)",
    border: "rgba(244,114,182,0.34)",
    glow: "rgba(236,72,153,0.2)",
    lightFill: "linear-gradient(180deg, rgba(251,207,232,0.88), rgba(255,255,255,0.2))",
    lightText: "rgba(157,23,77,0.96)",
    darkFill: "linear-gradient(180deg, rgba(244,114,182,0.2), rgba(80,7,36,0.84))",
    darkText: "rgba(252,231,243,0.96)",
  },
  pulse: {
    accent: "rgba(52,211,153,0.96)",
    border: "rgba(52,211,153,0.34)",
    glow: "rgba(16,185,129,0.2)",
    lightFill: "linear-gradient(180deg, rgba(209,250,229,0.88), rgba(255,255,255,0.2))",
    lightText: "rgba(6,95,70,0.96)",
    darkFill: "linear-gradient(180deg, rgba(52,211,153,0.2), rgba(6,78,59,0.84))",
    darkText: "rgba(209,250,229,0.98)",
  },
};

export default function NarrativeSonicsLensTags({
  availableLensIds,
  activeLensId = null,
  onToggleLens,
  lightMode = false,
}: NarrativeSonicsLensTagsProps): JSX.Element {
  return (
    <div className="flex flex-wrap items-center justify-end gap-1.5">
      {availableLensIds.map((lensId) => {
        const active = lensId === activeLensId;
        const visual = LENS_VISUALS[lensId];
        return (
          <button
            key={lensId}
            type="button"
            onClick={() => onToggleLens(lensId)}
            aria-pressed={active}
            className="inline-flex h-8 items-center rounded-full px-3 text-[10px] font-black tracking-[0.2em]"
            style={{
              background: active
                ? lightMode
                  ? visual.lightFill
                  : visual.darkFill
                : lightMode
                  ? "linear-gradient(180deg, rgba(255,255,255,0.28), rgba(226,232,240,0.24))"
                  : "linear-gradient(180deg, rgba(255,255,255,0.08), rgba(15,23,42,0.72))",
              border: active
                ? lightMode
                  ? `1px solid ${visual.border}`
                  : `1px solid ${visual.border}`
                : lightMode
                  ? "1px solid rgba(148,163,184,0.18)"
                  : "1px solid rgba(255,255,255,0.08)",
              color: active
                ? lightMode
                  ? visual.lightText
                  : visual.darkText
                : lightMode
                  ? "rgba(15,23,42,0.78)"
                  : "rgba(255,255,255,0.78)",
              boxShadow: active
                ? lightMode
                  ? `0 0 0 1px ${visual.border}, 0 10px 24px ${visual.glow}, inset 0 1px 0 rgba(255,255,255,0.48)`
                  : `0 10px 24px ${visual.glow}, inset 0 1px 0 rgba(255,255,255,0.1)`
                : "none",
              transform: active ? "translateY(-1px)" : "translateY(0px)",
              transition: "transform 160ms ease, box-shadow 180ms ease, background 180ms ease, border-color 180ms ease",
            }}
          >
            {LENS_LABELS[lensId]}
          </button>
        );
      })}
    </div>
  );
}